<template>
  <div class="space-y-1">
    <!-- Label -->
    <label 
      v-if="label"
      :for="fieldId"
      :class="labelClasses"
    >
      {{ label }}
      <span v-if="required" class="text-red-500 ml-1">*</span>
    </label>

    <!-- Conteneur du champ -->
    <div class="relative">
      <!-- Icône gauche -->
      <div 
        v-if="leftIcon"
        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
      >
        <component 
          :is="leftIcon" 
          :class="leftIconClasses"
        />
      </div>

      <!-- Champ de saisie -->
      <input
        :id="fieldId"
        :value="modelValue"
        :type="inputType"
        :name="name"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :readonly="readonly"
        :autocomplete="autocomplete"
        :class="inputClasses"
        :aria-invalid="hasError"
        :aria-describedby="hasError ? `${fieldId}-error` : hasHelpText ? `${fieldId}-help` : undefined"
        @input="handleInput"
        @blur="handleBlur"
        @focus="handleFocus"
      />

      <!-- Toggle visibilité -->
      <div 
        v-if="toggleable"
        class="absolute inset-y-0 right-0 pr-3 flex items-center"
      >
        <button
          type="button"
          :class="toggleButtonClasses"
          :aria-label="showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
          @click="togglePasswordVisibility"
        >
          <component 
            :is="showPassword ? EyeOffIcon : EyeIcon" 
            class="w-4 h-4"
          />
        </button>
      </div>
    </div>

    <!-- Indicateur de force -->
    <div 
      v-if="showStrengthIndicator && modelValue"
      class="mt-2"
    >
      <div class="flex justify-between items-center mb-1">
        <span class="text-xs text-gray-600">Force du mot de passe</span>
        <span :class="strengthLabelClasses">{{ strengthLabel }}</span>
      </div>
      <div class="w-full bg-gray-200 rounded-full h-2">
        <div 
          :class="strengthBarClasses"
          :style="{ width: `${strengthPercentage}%` }"
          class="h-2 rounded-full transition-all duration-300"
        ></div>
      </div>
    </div>

    <!-- Texte d'aide -->
    <p 
      v-if="hasHelpText && !hasError"
      :id="`${fieldId}-help`"
      class="text-sm text-gray-600"
    >
      {{ helpText }}
    </p>

    <!-- Message d'erreur -->
    <p 
      v-if="hasError"
      :id="`${fieldId}-error`"
      class="text-sm text-red-600"
      role="alert"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Eye as EyeIcon, EyeOff as EyeOffIcon } from 'lucide-vue-next';
import { authService } from '@/services/authService';

export interface PasswordFieldProps {
  modelValue: string;
  name?: string;
  label?: string;
  placeholder?: string;
  helpText?: string;
  errorMessage?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  autocomplete?: string;
  leftIcon?: unknown;
  size?: 'sm' | 'md' | 'lg';
  toggleable?: boolean;
  showStrengthIndicator?: boolean;
}

export interface PasswordFieldEmits {
  (e: 'update:modelValue', value: string): void;
  (e: 'blur', event: FocusEvent): void;
  (e: 'focus', event: FocusEvent): void;
  (e: 'strength-change', strength: { score: number; label: string; percentage: number }): void;
}

const props = withDefaults(defineProps<PasswordFieldProps>(), {
  size: 'md',
  modelValue: '',
  toggleable: true,
  showStrengthIndicator: false,
  name: undefined,
  label: undefined,
  placeholder: undefined,
  helpText: undefined,
  errorMessage: undefined,
  required: false,
  disabled: false,
  readonly: false,
  autocomplete: undefined,
  leftIcon: undefined,
});

const emit = defineEmits<PasswordFieldEmits>();

// État local
const showPassword = ref(false);
const isFocused = ref(false);
const fieldId = computed(() => props.name || `password-field-${Math.random().toString(36).substr(2, 9)}`);

// Gestion de la visibilité
const inputType = computed(() => {
  return showPassword.value ? 'text' : 'password';
});

const togglePasswordVisibility = (): void => {
  showPassword.value = !showPassword.value;
};

// Validation et force du mot de passe
const passwordStrength = computed(() => {
  if (!props.modelValue) {
    return { score: 0, label: '', percentage: 0 };
  }

  const validation = authService.validatePassword(props.modelValue);
  
  // Calculer le score de force (0-4)
  let score = 0;
  if (props.modelValue.length >= 8) score++;
  if (/[a-z]/.test(props.modelValue)) score++;
  if (/[A-Z]/.test(props.modelValue)) score++;
  if (/\d/.test(props.modelValue)) score++;
  if (/[^a-zA-Z\d]/.test(props.modelValue)) score++;

  // Réduire le score si la validation échoue
  if (!validation.isValid) {
    score = Math.max(0, score - 2);
  }

  const labels = ['', 'Très faible', 'Faible', 'Moyen', 'Fort', 'Très fort'];
  const percentages = [0, 20, 40, 60, 80, 100];

  return {
    score,
    label: labels[score] || '',
    percentage: percentages[score] || 0,
  };
});

const strengthLabel = computed(() => passwordStrength.value.label);
const strengthPercentage = computed(() => passwordStrength.value.percentage);

// Émettre les changements de force
watch(passwordStrength, (newStrength) => {
  if (props.showStrengthIndicator && props.modelValue) {
    emit('strength-change', newStrength);
  }
}, { deep: true });

// États computés
const hasError = computed(() => !!props.errorMessage);
const hasHelpText = computed(() => !!props.helpText);

// Classes computées
const labelClasses = computed(() => {
  const baseClasses = 'block text-sm font-medium';
  
  if (hasError.value) {
    return `${baseClasses} text-red-700`;
  }
  
  return `${baseClasses} text-gray-700`;
});

const inputClasses = computed(() => {
  const baseClasses = 'block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors';
  
  // Tailles
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-3 py-2',
    lg: 'px-4 py-3 text-lg',
  };
  
  // Padding pour les icônes
  let paddingClasses = sizeClasses[props.size];
  if (props.leftIcon) {
    paddingClasses = paddingClasses.replace('px-3', 'pl-10 pr-3');
  }
  if (props.toggleable) {
    paddingClasses = paddingClasses.replace('pr-3', 'pr-10');
  }
  
  // États
  let stateClasses = '';
  if (hasError.value) {
    stateClasses = 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500';
  } else if (isFocused.value) {
    stateClasses = 'border-blue-300 ring-1 ring-blue-500';
  }
  
  if (props.disabled) {
    stateClasses += ' bg-gray-50 text-gray-500 cursor-not-allowed';
  }
  
  if (props.readonly) {
    stateClasses += ' bg-gray-50';
  }
  
  return [baseClasses, paddingClasses, stateClasses].join(' ');
});

const leftIconClasses = computed(() => {
  const baseClasses = 'w-5 h-5';
  
  if (hasError.value) {
    return `${baseClasses} text-red-400`;
  }
  
  return `${baseClasses} text-gray-400`;
});

const toggleButtonClasses = computed(() => {
  const baseClasses = 'text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors';
  
  if (props.disabled) {
    return `${baseClasses} cursor-not-allowed opacity-50`;
  }
  
  return baseClasses;
});

const strengthLabelClasses = computed(() => {
  const score = passwordStrength.value.score;
  const baseClasses = 'text-xs font-medium';
  
  const colorClasses = {
    0: 'text-gray-500',
    1: 'text-red-600',
    2: 'text-orange-600',
    3: 'text-yellow-600',
    4: 'text-green-600',
    5: 'text-green-700',
  };
  
  return `${baseClasses} ${colorClasses[score as keyof typeof colorClasses] || 'text-gray-500'}`;
});

const strengthBarClasses = computed(() => {
  const score = passwordStrength.value.score;
  
  const colorClasses = {
    0: 'bg-gray-300',
    1: 'bg-red-500',
    2: 'bg-orange-500',
    3: 'bg-yellow-500',
    4: 'bg-green-500',
    5: 'bg-green-600',
  };
  
  return colorClasses[score as keyof typeof colorClasses] || 'bg-gray-300';
});

// Gestionnaires d'événements
const handleInput = (event: Event): void => {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', target.value);
};

const handleBlur = (event: FocusEvent): void => {
  isFocused.value = false;
  emit('blur', event);
};

const handleFocus = (event: FocusEvent): void => {
  isFocused.value = true;
  emit('focus', event);
};

// Expose
defineExpose({
  focus: () => {
    const input = document.getElementById(fieldId.value) as HTMLInputElement;
    input?.focus();
  },
  blur: () => {
    const input = document.getElementById(fieldId.value) as HTMLInputElement;
    input?.blur();
  },
});
</script> 